import { z } from "zod";

/**
 * The shared shape of a paginated, sortable list transport.
 *
 * Every list endpoint that a data table drives should be built from these rather
 * than hand-rolling `{limit, offset, sort}` per feature. Hand-rolling drifted on
 * the details that actually matter — whether the page resets when the ordering
 * changes, whether an unknown sort key can reach SQL, whether there is an
 * `ORDER BY` tiebreaker — and each list got those subtly differently.
 *
 * This module is deliberately Zod-only so the web app can import the same key
 * unions and defaults the server validates against.
 */

export const SORT_DIRECTIONS = ["asc", "desc"] as const;

export const sortDirectionSchema = z.enum(SORT_DIRECTIONS);

export type SortDirection = (typeof SORT_DIRECTIONS)[number];

/** A resolved ordering: one of a closed set of keys, plus a direction. */
export type ListSort<TSortKey extends string> = {
  key: TSortKey;
  direction: SortDirection;
};

/**
 * A sort schema over a closed set of keys.
 *
 * The key set is closed because the sort reaches a SQL `ORDER BY`: the transport
 * must only be able to name a column that exists, and adding a key has to force
 * the column mapping to be updated.
 */
export function sortSchema<
  const TSortKeys extends readonly [string, ...string[]],
>(sortKeys: TSortKeys) {
  return z.object({
    key: z.enum(sortKeys),
    direction: sortDirectionSchema,
  });
}

export const DEFAULT_PAGE_SIZE = 25;

/**
 * The largest page any list transport will serve. A larger limit is rejected at
 * the boundary rather than silently clamped, so a caller asking for more than this
 * gets a validation error instead of quietly receiving fewer rows than it asked
 * for and paging as though it had them all.
 */
export const MAX_PAGE_SIZE = 200;

export const pageInputSchema = z.object({
  limit: z
    .number()
    .int()
    .positive()
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.number().int().min(0).default(0),
});

export type PageInput = z.infer<typeof pageInputSchema>;

/**
 * The input schema for a paginated, sortable list procedure.
 *
 * `sort` is nullish so an unsorted request is legal and the service applies its
 * own documented default — which the UI must then initialise to, or the first
 * paint claims no ordering while the rows are ordered.
 */
export function paginatedListInputSchema<
  const TSortKeys extends readonly [string, ...string[]],
>(sortKeys: TSortKeys) {
  return pageInputSchema.extend({
    sort: sortSchema(sortKeys).nullish(),
  });
}

/**
 * What an offset-paginated list returns. `total` is the count matching the
 * filters before the page was taken, which is what the footer needs to know
 * whether a next page exists.
 */
export type PaginatedResult<TItem> = {
  items: TItem[];
  total: number;
};

/* ---------------------------------------------------------------------------
 * Unified page envelope.
 *
 * Two list-access patterns coexist and cannot collapse into one: offset-based
 * reads (random access, cheap `COUNT`) and cursor-based reads (forward-only,
 * for high-volume ClickHouse tables where `OFFSET`/`COUNT(*)` over hundreds of
 * millions of rows is prohibitive). Rather than pretend they're the same, the
 * envelope is a discriminated union on a named `mode`, so a single consumer
 * (the CLI pager, the web DataTable, MCP) can drive both while the shape stays
 * honest about which access pattern backs it. Each arm is additive to the
 * shape that family already returned (`{items,total}` / `{items,nextCursor}`).
 * ------------------------------------------------------------------------ */

export const PAGINATION_MODES = ["offset", "cursor"] as const;

export const paginationModeSchema = z.enum(PAGINATION_MODES);

export type PaginationMode = (typeof PAGINATION_MODES)[number];

/** A random-access page: knows its total, so a consumer can jump to any page. */
export type OffsetPage<TItem> = {
  mode: "offset";
  items: TItem[];
  total: number;
};

/**
 * A forward-only page. `nextCursor` is an OPAQUE string the client only ever
 * echoes back — never parses. Endpoints whose native cursor is composite (e.g.
 * `{lastActiveAt, taskId}`) or numeric encode it with {@link encodeCursor} so
 * every cursor on the wire is a single string regardless of its internals.
 */
export type CursorPage<TItem> = {
  mode: "cursor";
  items: TItem[];
  nextCursor: string | null;
};

export type PageEnvelope<TItem> = OffsetPage<TItem> | CursorPage<TItem>;

/** Zod schema for an offset page over a given item schema. */
export function offsetPageSchema<TItem extends z.ZodTypeAny>(
  itemSchema: TItem,
) {
  return z.object({
    mode: z.literal("offset"),
    items: z.array(itemSchema),
    total: z.number().int().min(0),
  });
}

/** Zod schema for a cursor page over a given item schema. */
export function cursorPageSchema<TItem extends z.ZodTypeAny>(
  itemSchema: TItem,
) {
  return z.object({
    mode: z.literal("cursor"),
    items: z.array(itemSchema),
    nextCursor: z.string().nullable(),
  });
}

/**
 * The input schema for a cursor-paginated, sortable list procedure — the
 * cursor-mode sibling of {@link paginatedListInputSchema}. `cursor` is the
 * opaque string produced by a prior page's `nextCursor`.
 */
export function cursorListInputSchema<
  const TSortKeys extends readonly [string, ...string[]],
>(sortKeys: TSortKeys) {
  return z.object({
    limit: z
      .number()
      .int()
      .positive()
      .max(MAX_PAGE_SIZE)
      .default(DEFAULT_PAGE_SIZE),
    cursor: z.string().nullish(),
    sort: sortSchema(sortKeys).nullish(),
  });
}

/**
 * Whether another page exists after this one. Offset pages derive it from the
 * total; cursor pages from the presence of a next cursor. `offset` is the
 * offset the current page was fetched at (ignored for cursor pages).
 */
export function pageHasNext<TItem>(
  page: PageEnvelope<TItem>,
  offset = 0,
): boolean {
  return page.mode === "offset"
    ? offset + page.items.length < page.total
    : page.nextCursor !== null;
}

/* ---------------------------------------------------------------------------
 * Opaque cursor codec.
 *
 * A cursor's internals (a string key, a composite object, a row number) are an
 * endpoint's private business; the client treats the cursor as an opaque token.
 * These encode the native payload to a single URL-safe base64 string and back,
 * validating on decode against the endpoint's own payload schema. Uses only
 * web-standard globals (`btoa`/`atob`/`TextEncoder`) so it runs in Cloudflare
 * Workers, the browser, and Node alike.
 * ------------------------------------------------------------------------ */

function toBase64Url(json: string): string {
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function fromBase64Url(cursor: string): string {
  const padded = cursor
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(cursor.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder().decode(bytes);
}

/** Encode a native cursor payload to the opaque wire string. */
export function encodeCursor<TPayload>(payload: TPayload): string {
  return toBase64Url(JSON.stringify(payload));
}

/**
 * Decode an opaque cursor string back to its native payload, validated against
 * the endpoint's own payload schema. Throws if the cursor is malformed or does
 * not match the schema (a caller should treat that as a bad-request, not a 500).
 */
export function decodeCursor<TSchema extends z.ZodTypeAny>(
  cursor: string,
  schema: TSchema,
): z.infer<TSchema> {
  return schema.parse(JSON.parse(fromBase64Url(cursor)));
}
